// cSpell:ignore TPLN

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { http } from "../../../utils/httpCommon";
import { useApiUrls } from "../../useApiUrl";
import { Trap, trapsSchema } from "../../../types/planning/trap.type";
import { Assignment } from "../useDeliveryRoutes.types";
import { toast } from "../../../utils/snackbarHelper";
import { inboundTrapsSchema } from "../../../types/planning/plannedTrap.type";
import { toastMessage } from "../../../constants/strings";
import { useSelectedServiceCenter } from "../../useSelectedServiceCenter";

import { plannedRoutesQueryKey } from "../usePlannedRoutes";
import { deliveryRoutesKeys } from "../useDeliveryRoutes";
import { safeParsePartial } from "../../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { useInvalidateAllQueryShipmentDetails } from "./useShipmentDetails";

type UnplannedTrapSummaryKeyProps = {
  sicId: number;
  planDate?: number;
  searchTerm?: string;
};

const keys = {
  getTraps: ({ sicId, planDate, searchTerm }: UnplannedTrapSummaryKeyProps) => [
    "traps",
    sicId,
    planDate,
    searchTerm,
  ],
  trapHeader: (sicId: number) => ["traps", "trap-header", sicId],
  inboundTraps: (sicId: number, date: number) => [
    "traps",
    "inbound",
    sicId,
    date,
  ],
  unplannedTrapsSummary: ({
    sicId,
    planDate,
    searchTerm,
  }: UnplannedTrapSummaryKeyProps) => [
    "traps",
    "unplanned",
    "summary",
    sicId,
    planDate,
    searchTerm,
  ],
};

type UseTrapsOptions = {
  sicId: number;
  searchTerm?: string;
  showEmptyTraps?: boolean;
  enabled?: boolean;
  planDate: number;
};

export function useTraps({
  sicId,
  searchTerm,
  showEmptyTraps,
  enabled = true,
  planDate,
}: UseTrapsOptions) {
  const hookUrls = useApiUrls();
  const { getTraps } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.getTraps({ sicId, planDate, searchTerm }),
    queryFn: async () => {
      const { data, status } = await http.get(getTraps, {
        params: { sicId, searchTerm, planDate },
      });

      if (status === 204) return [];
      return isSafeParseEnabled
        ? safeParsePartial(trapsSchema, data, "useTraps")
        : trapsSchema.parse(data);
    },
    enabled,
    select: (data) =>
      showEmptyTraps ? data : data.filter((trap) => trap.shipmentsCount > 0),
  });
}

type UseInboundTrapsOptions = {
  sicId: number;
  date: number;
};

export const useGetInboundTraps = ({ sicId, date }: UseInboundTrapsOptions) => {
  const hookUrls = useApiUrls();
  const { getInboundTraps } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.inboundTraps(sicId, date),
    queryFn: async () => {
      const { data, status } = await http.get(getInboundTraps(sicId, date));

      if (status === 204) return [];
      return isSafeParseEnabled
        ? safeParsePartial(inboundTrapsSchema, data, "useGetInboundTraps")
        : inboundTrapsSchema.parse(data);
    },
  });
};

export const useGetUnplannedTrapsSummary = (
  sicId: number,
  planDate?: number,
  searchTerm?: string,
  showEmptyTraps = true
) => {
  const hookUrls = useApiUrls();
  const { getTraps } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: true,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.unplannedTrapsSummary({
      sicId,
      planDate,
      searchTerm,
    }),
    queryFn: async () => {
      const { data, status } = await http.get(getTraps, {
        params: {
          sicId,
          searchTerm,
          notInStatus: ["CLDK", "TPLN"].join(","),
          planDate,
        },
      });
      if (status === 204) return [];
      return isSafeParseEnabled
        ? safeParsePartial(trapsSchema, data, "useGetUnplannedTrapsSummary")
        : trapsSchema.parse(data);
    },
    select: (data) =>
      showEmptyTraps ? data : data.filter((trap) => trap.shipmentsCount > 0),
  });
};

// Invalidate all traps queries
export function useInvalidateTraps() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ["traps"],
    });
}

// Invalidate queries after creating a trap
function useInvalidateQueriesAfterCreateTrap() {
  const queryClient = useQueryClient();

  const invalidateQueriesAfterCreateTrap = (
    sicId: number,
    unixDate: number,
    options?: {
      includeShipmentsSearch?: boolean;
    }
  ) => {
    // Invalidate planned routes (sidebar shows both routes and traps)
    queryClient.invalidateQueries({
      queryKey: plannedRoutesQueryKey(unixDate, sicId),
    });

    // Invalidate traps queries for Assign To dropdown
    queryClient.invalidateQueries({
      queryKey: keys.inboundTraps(sicId, unixDate),
    });

    // Invalidate trap list queries
    queryClient.invalidateQueries({
      queryKey: keys.getTraps({ sicId, planDate: unixDate }),
    });

    // Invalidate delivery routes summary (may be affected by trap creation)
    queryClient.invalidateQueries({
      queryKey: deliveryRoutesKeys.deliveryRoutesSummary(sicId, unixDate),
    });

    // Invalidate unassigned shipments as they may have changed
    queryClient.invalidateQueries({
      queryKey: ["unassigned", "shipments", unixDate, sicId],
    });

    // Conditionally invalidate shipment search
    if (options?.includeShipmentsSearch) {
      queryClient.invalidateQueries({
        queryKey: ["shipments", "search"],
      });
    }
  };

  return invalidateQueriesAfterCreateTrap;
}

export type CreateTrapRequest = {
  name: string;
  tractorEquipmentId: number | null;
  trailerEquipmentId: number | null;
  straightTruckEquipmentId: number | null;
  doorId: number | null;
  sicId: number;
  unixDate: number;
  assignments: Assignment[];
};

type UseCreateTrapArgs = {
  onSuccess: (data: Trap, variables: CreateTrapRequest) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
};

export const useCreateTrap = ({
  onSuccess,
  onError,
  onSettled,
}: UseCreateTrapArgs) => {
  const hookUrls = useApiUrls();
  const { createTrap } = hookUrls;
  const invalidateQueriesAfterCreateTrap =
    useInvalidateQueriesAfterCreateTrap();

  return useMutation({
    mutationFn: async (body: CreateTrapRequest) => {
      const url = createTrap(body.sicId, body.unixDate);
      const { data } = await http.post(url, body);
      return data;
    },
    onSuccess,
    onError,
    onSettled: async (data, error, variables) => {
      const { sicId, unixDate, assignments } = variables;
      const allShipmentIds = assignments.flatMap(
        (assignment) => assignment.shipmentIds
      );

      invalidateQueriesAfterCreateTrap(sicId, unixDate, {
        includeShipmentsSearch: allShipmentIds.length > 0,
      });

      // Call the custom onSettled callback if provided
      if (onSettled) {
        await onSettled();
      }
    },
  });
};

export const useDeleteTraps = (successHandler: () => void) => {
  type DeleteTrapsApiError = {
    code: "CANNOT_DELETE_TRAP";
    invalidTrapIds: number[];
    loadedTrapIds: number[];
  };

  const [serviceCenter] = useSelectedServiceCenter();
  const hookUrls = useApiUrls();
  const { deleteTraps } = hookUrls;
  return useMutation({
    mutationFn: async (traps: Trap[]) => {
      const trapIds = traps.map((trap) => trap.id);
      const url = deleteTraps(trapIds, serviceCenter.id);
      await http.delete(url);
    },
    onSuccess: successHandler,
    onError: (error, traps: Trap[]) => {
      if (!axios.isAxiosError(error)) {
        return toast(toastMessage.generics.error, {
          variant: "error",
        });
      }
      const deleteTrapsError: DeleteTrapsApiError = error.response?.data;

      const loadedTraps = traps.filter((trap) =>
        deleteTrapsError.loadedTrapIds.includes(trap.id)
      );
      if (loadedTraps.length > 0) {
        const multipleTraps = loadedTraps.length > 1;
        const trapNames = `"${loadedTraps
          .map((trap) => trap.name)
          .join('", "')}"`;

        const text = multipleTraps
          ? `Traps ${trapNames} are loaded, thus cannot be deleted.`
          : `Trap ${trapNames} is loaded, thus cannot be deleted.`;

        toast(text, {
          variant: "warning",
        });
      }

      const invalidTraps = traps.filter((trap) =>
        deleteTrapsError.invalidTrapIds.includes(trap.id)
      );
      if (invalidTraps.length > 0) {
        const multipleTraps = invalidTraps.length > 1;
        const trapNames = `"${invalidTraps
          .map((trap) => trap.name)
          .join('", "')}"`;

        const text = multipleTraps
          ? `Traps ${trapNames} are invalid, thus cannot be deleted.`
          : `Trap ${trapNames} is invalid, thus cannot be deleted.`;

        toast(text, {
          variant: "warning",
        });
      }
    },
  });
};

export const useDeleteTrapById = (
  trapId: number,
  sicId: number,
  onSuccess?: () => void
) => {
  const invalidateTraps = useInvalidateTraps();
  const hookUrls = useApiUrls();
  const { deleteTraps } = hookUrls;
  return useMutation({
    mutationFn: () => http.delete(deleteTraps([trapId], sicId)),
    onSuccess: () => {
      invalidateTraps();
      onSuccess?.();
    },
    onError: () =>
      toast(toastMessage.generics.error, {
        variant: "error",
      }),
  });
};

export type MergeTrapRequest = {
  trapIdsToMerge: (number | null)[];
  mergeIntoTrapId: number | null;
  name: string;
  trailerEquipmentId: number | null;
  straightTruckEquipmentId: number | null;
  doorId: number | null;
  date: number;
  sicId: number;
  customName: string | null;
};

export const useMergeTrap = (
  successHandler: (data: Trap, variables: MergeTrapRequest) => void
) => {
  const invalidateTraps = useInvalidateTraps();
  const invalidateAllQueryShipmentDetails =
    useInvalidateAllQueryShipmentDetails();
  const hookUrls = useApiUrls();
  const { postMergeTrap } = hookUrls;

  return useMutation({
    mutationFn: async (body: MergeTrapRequest) => {
      const url = postMergeTrap;
      const { data } = await http.post(url, body);
      return data;
    },
    onSuccess: successHandler,
    onSettled: () => {
      invalidateTraps();
      invalidateAllQueryShipmentDetails();
    },
  });
};
